import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, doc, getDoc, setDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject, listAll } from 'firebase/storage';
import { db, storage, auth } from '../../firebase';
import { onAuthStateChanged } from 'firebase/auth';
import FormatBar from '../format_bar/formatBar';
import PhotoFocus from '../photo_focus/photoFocus';
import './adminForside.css';

// Hjælpefunktion til at konvertere HTML til markdown
const htmlToMarkdown = (html) => {
  if (!html) return '';
  
  // Erstat HTML med markdown (simpel konvertering)
  let markdown = html;
  
  // Konverter overskrifter
  markdown = markdown.replace(/<h3>(.*?)<\/h3>/g, '## $1');
  
  // Konverter fed tekst
  markdown = markdown.replace(/<strong>(.*?)<\/strong>/g, '**$1**');
  
  // Konverter kursiv tekst
  markdown = markdown.replace(/<em>(.*?)<\/em>/g, '_$1_');
  
  // Konverter understregning
  markdown = markdown.replace(/<u>(.*?)<\/u>/g, '__$1__');
  
  // Konverter links
  markdown = markdown.replace(/<a href="(.*?)".*?>(.*?)<\/a>/g, '[$2]($1)');
  
  // Konverter lister
  markdown = markdown.replace(/<ul>(.*?)<\/ul>/gs, function(match, p1) {
    return p1.replace(/<li>(.*?)<\/li>/g, '- $1');
  });
  
  // Fjern p tags
  markdown = markdown.replace(/<p>(.*?)<\/p>/g, '$1\n\n');
  
  // Fjern diverse tags og rengør teksten
  markdown = markdown.replace(/<[^>]*>/g, '');
  markdown = markdown.replace(/&nbsp;/g, ' ');
  
  // Fjern ekstra linjeskift
  markdown = markdown.replace(/\n{3,}/g, '\n\n');
  
  return markdown.trim();
};

// Hjælpefunktion til at konvertere markdown til HTML
const markdownToHtml = (markdown) => {
  if (!markdown) return '';

  // Konverter overskrifter
  let html = markdown.replace(/^## (.*?)$/gm, '<h3>$1</h3>');

  // Konverter fed tekst
  html = html.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
  
  // Konverter kursiv tekst
  html = html.replace(/_(.*?)_/g, '<em>$1</em>');
  
  // Konverter understregning
  html = html.replace(/__(.*?)__/g, '<u>$1</u>');

  // Konverter links - [text](url)
  html = html.replace(/\[(.*?)\]\((.*?)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>');

  // Konverter bullet points
  html = html.replace(/^- (.*?)$/gm, '<li>$1</li>');
  html = html.split(/\n/).map(line => {
    if (line.startsWith('<li>')) {
      return line;
    }
    return line === '' ? '<br/>' : `<p>${line}</p>`;
  }).join('');

  // Wrap bullets i ul tags
  html = html.replace(/(<li>.*?<\/li>)+/g, '<ul>$&</ul>');

  // Fjern ekstra <br/> tags
  html = html.replace(/<p><br\/><\/p>/g, '<br/>');
  html = html.replace(/<br\/><br\/>/g, '<br/>');

  return html;
};

const AdminForside = () => {
  const navigate = useNavigate();
  const editorRef = useRef(null);

  // Auth state
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // Data states
  const [heroImages, setHeroImages] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [welcomeText, setWelcomeText] = useState({
    title: '',
    content: '',
    htmlContent: ''
  });
  const [isEditingWelcomeText, setIsEditingWelcomeText] = useState(false);
  const [editedWelcomeText, setEditedWelcomeText] = useState({
    title: '',
    content: '',
    htmlContent: ''
  });
  // Aktive formateringer
  const [activeFormats, setActiveFormats] = useState({
    bold: false,
    italic: false,
    heading: false,
    bullet: false,
    link: false,
    underline: false
  });

  // UI states
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [newImage, setNewImage] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  
  // Tilføj ny state for PhotoFocus
  const [focusImage, setFocusImage] = useState(null);
  const [showPhotoFocus, setShowPhotoFocus] = useState(false);

  // Check auth
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setUser(user);
      setIsLoading(false);
      if (!user) {
        navigate('/admin/login');
      }
    });

    return () => unsubscribe();
  }, [navigate]);

  // Load content
  useEffect(() => {
    const fetchContent = async () => {
      try {
        // Fetch hero images
        const heroDocRef = doc(db, 'content', 'hero');
        const heroDoc = await getDoc(heroDocRef);
        
        if (heroDoc.exists()) {
          let images = heroDoc.data().images || [];
          
          // Sørg for at alle billeder har et navn
          const imagesWithNames = images.map(image => {
            if (!image.name && image.url) {
              // Forsøg at udtrække filnavn fra URL
              try {
                const url = new URL(image.url);
                const pathSegments = decodeURIComponent(url.pathname).split('/');
                // Tag det sidste segment af stien som filnavn
                const fileName = pathSegments[pathSegments.length - 1];
                if (fileName) {
                  return {
                    ...image,
                    name: fileName.split('?')[0] // Fjern eventuelle query parametre
                  };
                }
              } catch (e) {
                console.error("Kunne ikke udtrække filnavn fra URL:", e);
              }
            }
            return image;
          });
          
          // Gem opdaterede billeder med navne tilbage til Firestore hvis der var ændringer
          const hasChanges = JSON.stringify(images) !== JSON.stringify(imagesWithNames);
          if (hasChanges) {
            await setDoc(doc(db, 'content', 'hero'), {
              images: imagesWithNames,
              lastUpdated: new Date()
            });
          }
          
          setHeroImages(imagesWithNames);
        }
        
        // Fetch welcome text
        const welcomeDocRef = doc(db, 'content', 'welcomeText');
        const welcomeDoc = await getDoc(welcomeDocRef);
        
        if (welcomeDoc.exists()) {
          const data = welcomeDoc.data();
          // Hvis vi har HTML-indhold, brug det, ellers konverter markdown til HTML
          const html = data.htmlContent || markdownToHtml(data.content || '');
          
            setWelcomeText({
              title: data.title || '',
            content: data.content || '',
            htmlContent: html
            });
        }
        
      } catch (error) {
        console.error('Error fetching content:', error);
        setMessage({
          text: 'Der opstod en fejl ved indlæsning af indhold',
          type: 'error'
        });
      }
    };

    if (user) {
      fetchContent();
    }
  }, [user]);

  // Tilføj eventlisteners når komponenten mountes
  useEffect(() => {
    const handleSelectionChange = () => {
      checkActiveFormats();
    };
    
    document.addEventListener('selectionchange', handleSelectionChange);
    
    return () => {
      document.removeEventListener('selectionchange', handleSelectionChange);
    };
  }, []);

  // Tilføj en effect til at håndtere klik i editoren
  useEffect(() => {
    if (isEditingWelcomeText && editorRef.current) {
      const handleEditorClick = () => {
        // Når der klikkes i editoren, tjek aktive formateringer
        setTimeout(() => {
          checkActiveFormats();
        }, 0);
      };
      
      // Lyt efter klik og mouseup events
      editorRef.current.addEventListener('click', handleEditorClick);
      editorRef.current.addEventListener('mouseup', handleEditorClick);
      
      return () => {
        if (editorRef.current) {
          editorRef.current.removeEventListener('click', handleEditorClick);
          editorRef.current.removeEventListener('mouseup', handleEditorClick);
        }
      };
    }
  }, [isEditingWelcomeText]);

  // Initialiser editor indhold
  useEffect(() => {
    if (isEditingWelcomeText && editorRef.current) {
      editorRef.current.innerHTML = editedWelcomeText.htmlContent;
      
      // Tilføj en lille forsinkelse for at sikre, at editoren er indlæst korrekt
      setTimeout(() => {
        checkActiveFormats();
      }, 100);
    }
  }, [isEditingWelcomeText]);

  const handleFileChange = (e) => {
    if (e.target.files[0]) {
      const file = e.target.files[0];
      setSelectedFile(file);
      
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) return;
    
    setUploading(true);
    try {
      const storageRef = ref(storage, `hero-images/${selectedFile.name}`);
      await uploadBytes(storageRef, selectedFile);
      const downloadURL = await getDownloadURL(storageRef);
      
      // Udtrække filnavn fra stien for at sikre konsistens
      let fileName = selectedFile.name;
      try {
        // Vi gemmer også filnavnet fra storage reference stien for at sikre korrekt filnavn
        const fileNameFromPath = storageRef.name;
        if (fileNameFromPath) {
          fileName = fileNameFromPath;
        }
      } catch (e) {
        console.error("Kunne ikke udtrække filnavn fra reference:", e);
      }
      
      // Opdater array med nye billeder
      const updatedImages = [...heroImages, { url: downloadURL, name: fileName }];
      setHeroImages(updatedImages);
      
      // Gem opdateret billede-array i Firestore
      await setDoc(doc(db, 'content', 'hero'), {
        images: updatedImages,
        lastUpdated: new Date()
      });
      
      setMessage({ type: 'success', text: 'Billede blev uploadet med succes!' });
      
      // Reset upload state
      setSelectedFile(null);
      setPreview(null);
    } catch (error) {
      console.error("Fejl ved upload:", error);
      setMessage({ type: 'error', text: 'Fejl ved upload af billede.' });
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteImage = async (imageName, imageUrl) => {
    if (window.confirm('Er du sikker på, at du vil slette dette billede?')) {
      try {
        // Hvis billednavnet ikke findes, forsøg at udtrække det fra URL'en
        let fileNameToDelete = imageName;
        
        if (!fileNameToDelete && imageUrl) {
          // Forsøg at udtrække filnavnet fra URL'en
          try {
            const url = new URL(imageUrl);
            const pathSegments = decodeURIComponent(url.pathname).split('/');
            // Tag det sidste segment af stien som filnavn
            const fileName = pathSegments[pathSegments.length - 1];
            if (fileName) {
              fileNameToDelete = fileName.split('?')[0]; // Fjern eventuelle query parametre
            }
          } catch (e) {
            console.error("Kunne ikke udtrække filnavn fra URL:", e);
          }
        }
        
        if (!fileNameToDelete) {
          throw new Error("Kunne ikke identificere billednavnet");
        }
        
        // Slet filen fra storage
        const imageRef = ref(storage, `hero-images/${fileNameToDelete}`);
        await deleteObject(imageRef);
        
        // Opdater array uden det slettede billede - brug URL til at identificere hvis navn mangler
        const updatedImages = imageUrl 
          ? heroImages.filter(img => img.url !== imageUrl)
          : heroImages.filter(img => img.name !== imageName);
          
        setHeroImages(updatedImages);
        
        // Gem opdateret billede-array i Firestore
        await setDoc(doc(db, 'content', 'hero'), {
          images: updatedImages,
          lastUpdated: new Date()
        });
        
        setMessage({ type: 'success', text: 'Billede blev slettet!' });
      } catch (error) {
        console.error("Fejl ved sletning:", error);
        setMessage({ type: 'error', text: 'Fejl ved sletning af billede.' });
      }
    }
  };

  // Funktioner til håndtering af rich text editing
  const handleEditWelcomeText = () => {
    setEditedWelcomeText({...welcomeText});
    setIsEditingWelcomeText(true);
    
    // Kør format-opdatering når redigeringen starter
    setTimeout(() => {
      if (editorRef.current) {
        // Sikr at editoren får fokus
        editorRef.current.focus();
        
        // Force en første formaterings-opdatering
        forceFormatCheck();
      }
    }, 100);
  };
  
  // Force opdatering af formatering
  const forceFormatCheck = () => {
    if (!editorRef.current) return;
    
    // Nulstil formatering først for at sikre en frisk opdatering
    setActiveFormats({
      bold: false,
      italic: false,
      heading: false,
      bullet: false,
      link: false,
      underline: false
    });
    
    // Kør derefter tjek af aktive formateringer
    setTimeout(() => {
      checkActiveFormats();
    }, 10);
  };

  const handleWelcomeTextChange = (e) => {
    const { name, value } = e.target;
    setEditedWelcomeText(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const saveSelection = () => {
    const selection = window.getSelection();
    if (selection && selection.rangeCount > 0) {
      const range = selection.getRangeAt(0);
      // Tjek om range er inden for editoren
      if (editorRef.current && editorRef.current.contains(range.commonAncestorContainer)) {
        return range;
      }
    }
    return null;
  };

  const restoreSelection = (range) => {
    if (range && editorRef.current && editorRef.current.contains(range.commonAncestorContainer)) {
      const selection = window.getSelection();
      selection.removeAllRanges();
      try {
        selection.addRange(range);
      } catch (e) {
        console.warn('Kunne ikke gendanne selektion:', e);
      }
    }
  };

  const handleHtmlContentChange = (e) => {
    if (!editorRef.current) return;
    
    // Hent HTML indhold fra editoren
    const html = editorRef.current.innerHTML;
    
    // Opdater state
    setEditedWelcomeText(prev => ({
      ...prev,
      htmlContent: html,
      content: htmlToMarkdown(html)
    }));
    
    // Opdater aktive formateringer
    setTimeout(() => {
      checkActiveFormats();
    }, 0);
  };

  const handleEditorFocus = () => {
    if (editorRef.current) {
      // Sikre at editoren får fokus
      editorRef.current.focus();
      
      // Tjek aktive formateringer ved fokus
      checkActiveFormats();
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      document.execCommand('insertParagraph', false, null);
      e.preventDefault();
    }
    
    // Opdater formateringer efter særlige taster
    if (e.key === 'Delete' || e.key === 'Backspace') {
      setTimeout(() => {
        checkActiveFormats();
      }, 10);
    }
    
    // Tjek formatering ved kombination med Ctrl
    if (e.ctrlKey) {
      switch (e.key.toLowerCase()) {
        case 'b': // Bold
        case 'i': // Italic
        case 'u': // Underline
          setTimeout(() => {
            checkActiveFormats();
          }, 10);
          break;
        default:
          break;
      }
    }
  };

  const handleSaveWelcomeText = async () => {
    try {
      await setDoc(doc(db, "content", "welcomeText"), {
        title: editedWelcomeText.title,
        content: editedWelcomeText.content, // Behold markdown for bagudkompatibilitet
        htmlContent: editedWelcomeText.htmlContent, // Gem HTML-indhold
        lastUpdated: new Date()
      });
      
      setWelcomeText({...editedWelcomeText});
      setIsEditingWelcomeText(false);
      setMessage({ type: 'success', text: 'Velkomsttekst opdateret!' });
    } catch (error) {
      console.error("Fejl ved opdatering af velkomsttekst:", error);
      setMessage({ type: 'error', text: 'Fejl ved gem af velkomsttekst.' });
    }
  };

  const handleCancelEdit = () => {
    setIsEditingWelcomeText(false);
  };

  const closeMessage = () => {
    setMessage({ type: '', text: '' });
  };

  const goBack = () => {
    navigate('/admin');
  };

  // Funktion til at tjekke aktive formateringer
  const checkActiveFormats = () => {
    const selection = window.getSelection();
    
    // Hvis der ikke er en reference til editoren, returner
    if (!editorRef.current) return;
    
    // Vi bruger en anden strategi end document.queryCommandState for inline formateringer,
    // da den ikke altid virker korrekt før den er brugt mindst én gang

    // Initialiser formatering til false
    let isBold = false;
    let isItalic = false;
    let isUnderline = false;
    let isBullet = false;
    let isHeading = false;
    let isLink = false;
    
    // Få fat i det element, hvor cursoren står
    let currentNode = null;
    
    if (selection && selection.rangeCount > 0) {
      currentNode = selection.anchorNode;
      
      // Tjek om editoren indeholder selektionen
      if (!editorRef.current.contains(currentNode)) {
        return;
      }
    } else if (editorRef.current.firstChild) {
      // Hvis der ikke er en selektion, brug første barn
      currentNode = editorRef.current.firstChild;
    } else {
      // Hvis editoren er tom, returner standardværdien
      setActiveFormats({
        bold: false,
        italic: false,
        heading: false,
        bullet: false,
        link: false,
        underline: false
      });
      return;
    }
    
    // Tjek formateringer ved at traversere op gennem DOM-træet
    while (currentNode && currentNode !== editorRef.current) {
      if (currentNode.nodeType === Node.ELEMENT_NODE) {
        const nodeName = currentNode.nodeName.toLowerCase();
        const computedStyle = window.getComputedStyle(currentNode);
        
        if (nodeName === 'strong' || nodeName === 'b' || computedStyle.fontWeight === 'bold' || parseInt(computedStyle.fontWeight) >= 700) {
          isBold = true;
        }
        
        if (nodeName === 'em' || nodeName === 'i' || computedStyle.fontStyle === 'italic') {
          isItalic = true;
        }
        
        if (nodeName === 'u' || computedStyle.textDecoration.includes('underline')) {
          isUnderline = true;
        }
        
        if (nodeName === 'h3') {
          isHeading = true;
        }
        
        if (nodeName === 'a') {
          isLink = true;
        }
        
        if (nodeName === 'ul' || nodeName === 'li') {
          isBullet = true;
        }
      }
      
      // Gå op til forældrenoden
      currentNode = currentNode.parentNode;
    }
    
    // Opdater state med de aktive formateringer
    setActiveFormats({
      bold: isBold,
      italic: isItalic,
      heading: isHeading,
      bullet: isBullet,
      link: isLink,
      underline: isUnderline
    });
  };

  const isValidUrl = (text) => {
    try {
      // Tjek om teksten starter med http:// eller https:// eller www.
      if (text.match(/^(https?:\/\/|www\.)/i)) {
        // Hvis den starter med www., tilføj https://
        const url = text.startsWith('www.') ? `https://${text}` : text;
        new URL(url);
        return url;
      }
      return false;
    } catch {
      return false;
    }
  };

  const handleFormatClick = (formatType) => {
    if (!editorRef.current) return;
    
    // Fokuser på editoren hvis den ikke allerede har fokus
    if (!editorRef.current.contains(document.activeElement)) {
      editorRef.current.focus();
    }

    // Gem den aktuelle selektion
    const selection = window.getSelection();
    if (!selection.rangeCount) return;
    
    const range = selection.getRangeAt(0);
    const selectedText = range.toString().trim();
    
    // Tjek om selektionen er inden for editoren
    if (!editorRef.current.contains(range.commonAncestorContainer)) {
      return;
    }

    // Opdater formateringen i editoren
    switch (formatType) {
      case 'bold':
        applyInlineFormatting('bold', activeFormats.bold);
        break;
      
      case 'italic':
        applyInlineFormatting('italic', activeFormats.italic);
        break;
      
      case 'underline':
        applyInlineFormatting('underline', activeFormats.underline);
        break;
      
      case 'heading':
        const isHeading = activeFormats.heading;
        
        // For H3 bruger vi stadig execCommand da det er mere pålideligt her
        document.execCommand('formatBlock', false, isHeading ? 'p' : 'h3');
        break;
      
      case 'bullet':
        // For punktopstilling bruger vi stadig execCommand
        document.execCommand('insertUnorderedList', false);
        break;

      case 'link':
        // For links bruger vi vores egen implementering
        handleLinkInsertion(selectedText);
        break;
    }
    
    // Opdater HTML indhold og tjek aktive formateringer
    handleHtmlContentChange();
    
    // Force opdatering af formaterings-status
    setTimeout(() => {
      forceFormatCheck();
    }, 0);
  };

  // Ny funktion til at håndtere inline formatering
  const applyInlineFormatting = (formatType, isCurrentlyActive) => {
    const selection = window.getSelection();
    if (!selection.rangeCount) return;
    
    const range = selection.getRangeAt(0);
    let selectedText = range.toString();
    
    // Hvis der ikke er valgt noget tekst, prøv at lave en default selektion
    if (!selectedText) {
      // Indsæt en placeholder tekst hvis der ikke er tekst i editoren
      if (!editorRef.current.textContent.trim()) {
        // Indsæt en placeholder tekst
        const placeholderText = 'Tekst';
        editorRef.current.textContent = placeholderText;
        
        // Vælg denne tekst
        const newRange = document.createRange();
        newRange.selectNodeContents(editorRef.current.firstChild);
        selection.removeAllRanges();
        selection.addRange(newRange);
        
        // Opdater range og selectedText
        selectedText = placeholderText;
      } else {
        // Brugeren har ikke markeret tekst, så vi gør ingenting
        return;
      }
    }
    
    // Bestem hvilket HTML-tag der skal bruges
    let tagName = '';
    switch (formatType) {
      case 'bold':
        tagName = 'strong';
        break;
      case 'italic':
        tagName = 'em';
        break;
      case 'underline':
        tagName = 'u';
        break;
      default:
        return;
    }
    
    // Få den opdaterede range
    const currentRange = selection.getRangeAt(0);
    
    // Hvis formateringen allerede er aktiv (og vi vil fjerne den)
    if (isCurrentlyActive) {
      // Brug removeFormatting til at fjerne formateringen
      removeFormatting(currentRange, tagName);
    } else {
      // Tilføj formatering
      // Tjek først om vi er inde i et element med en anden formatering
      let isInFormattedNode = false;
      let currentNode = selection.anchorNode;
      
      // Find ud af om vi er i et formateret element
      while (currentNode && currentNode !== editorRef.current) {
        if (currentNode.nodeType === Node.ELEMENT_NODE) {
          const nodeName = currentNode.nodeName.toLowerCase();
          if (['strong', 'em', 'u', 'a', 'li', 'h3'].includes(nodeName)) {
            isInFormattedNode = true;
            break;
          }
        }
        currentNode = currentNode.parentNode;
      }
      
      // Hvis vi er i et formateret element, brug execCommand som kan håndtere nested formatering
      if (isInFormattedNode) {
        switch (formatType) {
          case 'bold':
            document.execCommand('bold', false);
            break;
          case 'italic':
            document.execCommand('italic', false);
            break;
          case 'underline':
            document.execCommand('underline', false);
            break;
          default:
            break;
        }
      } else {
        // Ellers brug vores egen DOM-manipulation
        const newElement = document.createElement(tagName);
        
        // Opret et DocumentFragment med den valgte tekst
        currentRange.deleteContents();
        newElement.appendChild(document.createTextNode(selectedText));
        currentRange.insertNode(newElement);
        
        // Opdater selektion til at omfatte den nye node
        currentRange.selectNodeContents(newElement);
        selection.removeAllRanges();
        selection.addRange(currentRange);
      }
    }
    
    // Opdater indhold og formatering
    handleHtmlContentChange();
    
    // Opdater formateringer efter ændring
    setTimeout(() => {
      checkActiveFormats();
    }, 10);
  };

  // Funktion til at fjerne formatering
  const removeFormatting = (range, tagName) => {
    const selection = window.getSelection();
    
    // Tjek for gyldig selektion
    if (!selection || !selection.rangeCount) return;
    
    // Gem den markerede tekst før vi ændrer noget
    const selectedText = range.toString();
    
    // Funktion til at fjerne det angivne tag fra alle tekstnoder i en selection range
    const unwrapNode = (node, tagToRemove) => {
      if (!node) return;
      
      // Hvis den nuværende node er af den valgte tagType
      if (node.nodeType === Node.ELEMENT_NODE && node.nodeName.toLowerCase() === tagToRemove.toLowerCase()) {
        // Skab et dokumentfragment til at holde børnene
        const fragment = document.createDocumentFragment();
        
        // Flyt alle børn til fragmentet
        while (node.firstChild) {
          fragment.appendChild(node.firstChild);
        }
        
        // Erstat den originale node med fragmentet
        node.parentNode.replaceChild(fragment, node);
        return true;
      }
      
      return false;
    };
    
    // Find alle elementer i den aktuelle selektion
    const findNodesInRange = (range) => {
      // Find common ancestor container
      let container = range.commonAncestorContainer;
      
      // Hvis det er en tekstnode, gå op til dens parent
      if (container.nodeType === Node.TEXT_NODE) {
        container = container.parentNode;
      }
      
      // Find alle noder i container som skal tjekkes
      const allNodes = [];
      const nodeIterator = document.createNodeIterator(
        container,
        NodeFilter.SHOW_ELEMENT
      );
      
      let currentNode;
      // Iterér igennem alle noder og find dem med den søgte tag
      while ((currentNode = nodeIterator.nextNode())) {
        if (currentNode.nodeName.toLowerCase() === tagName.toLowerCase()) {
          // Tjek om noden overlapper med vores range
          const nodeRange = document.createRange();
          try {
            nodeRange.selectNode(currentNode);
            if (range.intersectsNode(currentNode)) {
              allNodes.push(currentNode);
            }
          } catch (e) {
            console.error("Fejl ved at tjekke node:", e);
          }
        }
      }
      
      return allNodes;
    };
    
    // Find alle noder med det angivne tag
    const nodesToUnwrap = findNodesInRange(range);
    
    // Gem det oprindelige indhold fra editoren
    const originalHTML = editorRef.current.innerHTML;
    
    // Hvis vi fandt noder med formateringen
    if (nodesToUnwrap.length > 0) {
      // Unwrap hver fundet node
      nodesToUnwrap.forEach(node => unwrapNode(node, tagName));
      
      // Ellers prøv med execCommand som fallback
    } else {
      // Tjek om vi er direkte i et element med den søgte formatering
      let currentNode = selection.anchorNode;
      let found = false;
      
      // Tjek alle parent nodes
      while (currentNode && currentNode !== editorRef.current) {
        if (currentNode.nodeType === Node.ELEMENT_NODE && 
            currentNode.nodeName.toLowerCase() === tagName.toLowerCase()) {
          found = true;
          unwrapNode(currentNode, tagName);
          break;
        }
        currentNode = currentNode.parentNode;
      }
      
      // Hvis intet blev fundet, brug execCommand som fallback
      if (!found) {
        switch (tagName.toLowerCase()) {
          case 'strong':
          case 'b':
            document.execCommand('bold', false);
            break;
          case 'em':
          case 'i':
            document.execCommand('italic', false);
            break;
          case 'u':
            document.execCommand('underline', false);
            break;
        }
      }
    }
    
    // Hvis indholdet er ændret, opdater state
    if (originalHTML !== editorRef.current.innerHTML) {
      // Undgå dobbelt kald til handleHtmlContentChange, som opdateres ved slutningen af funktionen
      editorRef.current.innerHTML = editorRef.current.innerHTML;
    }
    
    // Gendan markeringen ved at finde og markere den samme tekst
    if (selectedText.trim()) {
      // Funktion til at finde den tekst vi havde markeret før
      const findAndSelectText = () => {
        // Vi bruger en TreeWalker til at gennemgå alle tekstnoder
        const treeWalker = document.createTreeWalker(
          editorRef.current,
          NodeFilter.SHOW_TEXT,
          null,
          false
        );
        
        // Vi søger gennem alle tekstnoder for at finde vores markerede tekst
        while (treeWalker.nextNode()) {
          const node = treeWalker.currentNode;
          const nodeText = node.textContent;
          
          // Find positionen af vores tekst i denne node
          const position = nodeText.indexOf(selectedText);
          
          if (position >= 0) {
            // Vi fandt teksten, opret en ny range og sæt den som aktiv
            const newRange = document.createRange();
            newRange.setStart(node, position);
            newRange.setEnd(node, position + selectedText.length);
            
            selection.removeAllRanges();
            selection.addRange(newRange);
            return true;
          }
        }
        
        return false;
      };
      
      // Prøv at finde og markere teksten
      const foundText = findAndSelectText();
      
      // Hvis vi ikke kunne finde teksten, prøv en alternativ tilgang
      if (!foundText) {
        // Alternativ metode - marker den position hvor vores cursor var før
        try {
          // Marker der hvor cursoren var før
          const newPosition = window.getSelection().anchorOffset;
          const textNode = window.getSelection().anchorNode;
          
          if (textNode && textNode.nodeType === Node.TEXT_NODE) {
            const newRange = document.createRange();
            const textLength = Math.min(newPosition + selectedText.length, textNode.length);
            
            newRange.setStart(textNode, newPosition);
            newRange.setEnd(textNode, textLength);
            
            selection.removeAllRanges();
            selection.addRange(newRange);
          }
        } catch (e) {
          console.warn("Kunne ikke gendanne selektion efter formaterings-fjernelse", e);
        }
      }
    }
    
    // Opdater formatering til sidst
    handleHtmlContentChange();
  };

  // Håndter link-indsættelse
  const handleLinkInsertion = (selectedText) => {
    // Tjek om den valgte tekst er en URL
    const url = isValidUrl(selectedText) ? selectedText : prompt('Indtast URL:', 'https://');
    
    if (url) {
      const linkText = selectedText || prompt('Indtast link tekst:', '');
      
      if (linkText) {
        // Indsæt link direkte i HTML
        const linkElement = document.createElement('a');
        linkElement.href = url;
        linkElement.target = '_blank';
        linkElement.rel = 'noopener noreferrer';
        linkElement.textContent = linkText;
        
        // Indsæt linket
        const selection = window.getSelection();
        const range = selection.getRangeAt(0);
        range.deleteContents();
        range.insertNode(linkElement);
        
        // Flyt cursoren efter linket
        range.setStartAfter(linkElement);
        range.setEndAfter(linkElement);
        selection.removeAllRanges();
        selection.addRange(range);
      }
    }
    
    // Opdater indhold
    handleHtmlContentChange();
  };

  // Håndter klik på fokusknap
  const handleFocusClick = (image) => {
    setFocusImage(image);
    setShowPhotoFocus(true);
  };

  // Håndter gem af fokuspunkt
  const handleSaveFocusPoint = (imageUrl, focusPoint) => {
    // Opdater lokalt heroImages state med det nye fokuspunkt
    const updatedHeroImages = heroImages.map(img => {
      if (img.url === imageUrl) {
        return { ...img, focusPoint };
      }
      return img;
    });
    
    setHeroImages(updatedHeroImages);
    setShowPhotoFocus(false);
    setMessage({ type: 'success', text: 'Billedefokus blev gemt!' });
  };

  if (isLoading) {
    return (
      <div className="admin-loading">
        <div className="loading-spinner"></div>
        <p>Indlæser...</p>
      </div>
    );
  }

  return (
    <div className="admin-container">
      <header className="admin-header">
        <div className="admin-logo">
          <img 
            src="/media/logo/psykologiskperspektiv-logo.avif" 
            alt="Psykologisk Perspektiv" 
            onClick={() => navigate('/admin')}
            style={{ cursor: 'pointer' }}
          />
        </div>
        <div className="admin-header-right">
          <div className="admin-user">
            <span>Logget ind som: </span>
            <strong>{user.email}</strong>
          </div>
          <button onClick={() => navigate('/admin')} className="back-button">
            ← Tilbage
          </button>
        </div>
      </header>
      
      <main className="admin-content">
        <div className="admin-forside-container">
          <button className="back-button" onClick={goBack}>
            ← Tilbage til Admin
          </button>
          
          <h1>Administrer Forside</h1>
          
          {message.text && (
            <div className={`admin-message ${message.type}`}>
              <p>{message.text}</p>
              <button 
                className="close-message" 
                onClick={closeMessage}
              >
                ×
              </button>
            </div>
          )}
          
          <div className="admin-section">
            <h2>Karruselbilleder</h2>
            <p className="admin-description">
              Her kan du tilføje, redigere eller slette billederne i karrusellen øverst på forsiden.
              Du kan også vælge et fokuspunkt til hvert billede.
            </p>
            
            <div className="hero-images-grid">
              {heroImages.map((image, index) => (
                <div key={index} className="hero-image-item">
                  <img src={image.url} alt={`Karruselbillede ${index + 1}`} />
                  <div className="hero-image-actions">
                    <button 
                      className="focus-image-btn"
                      onClick={() => handleFocusClick(image)}
                    >
                      Fokus
                    </button>
                    <button 
                      className="delete-image-btn"
                      onClick={() => handleDeleteImage(image.name, image.url)}
                    >
                      Slet
                    </button>
                  </div>
                </div>
              ))}
            </div>
            
            <div className="admin-hero-upload-container">
              <h3 className="admin-hero-upload-title">Tilføj nyt billede</h3>
              <div className="admin-hero-upload-controls">
                {!preview ? (
                  <label className="admin-hero-select-btn">
                    Vælg Billede
                    <input 
                      type="file" 
                      accept="image/*" 
                      onChange={handleFileChange}
                      id="image-upload"
                      className="admin-hero-file-input"
                      disabled={uploading}
                    />
                  </label>
                ) : (
                  <div className="admin-hero-preview-container">
                    <img src={preview} alt="Forhåndsvisning" className="admin-hero-preview-image" />
                    <div className="admin-hero-preview-buttons">
                      <button 
                        className="admin-hero-upload-btn"
                        onClick={handleUpload}
                        disabled={uploading}
                      >
                        {uploading ? 'Uploader...' : 'Upload billede'}
                      </button>
                      <button 
                        className="admin-hero-cancel-btn"
                        onClick={() => {
                          setSelectedFile(null);
                          setPreview(null);
                        }}
                        disabled={uploading}
                      >
                        Annuller
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
          
          <div className="admin-section">
            <h2>Velkomsttekst</h2>
            <p className="admin-description">
              Her kan du redigere velkomstteksten på forsiden.
            </p>
            
            {!isEditingWelcomeText ? (
              <div className="welcome-text-preview">
                <h3>{welcomeText.title}</h3>
                <div className="welcome-text-content" dangerouslySetInnerHTML={{ __html: welcomeText.htmlContent }} />
                <button 
                  className="edit-content-btn"
                  onClick={handleEditWelcomeText}
                >
                  Rediger Velkomsttekst
                </button>
              </div>
            ) : (
              <div className="welcome-text-editor">
                <div className="form-group">
                  <label htmlFor="title">Overskrift</label>
                  <input
                    type="text"
                    id="title"
                    name="title"
                    value={editedWelcomeText.title}
                    onChange={handleWelcomeTextChange}
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="content">Indhold</label>
                  <FormatBar onFormatClick={handleFormatClick} activeFormats={activeFormats} />
                  
                  <div
                    ref={editorRef}
                    className="rich-text-editor"
                    contentEditable="true"
                    onInput={handleHtmlContentChange}
                    onFocus={handleEditorFocus}
                    onClick={() => checkActiveFormats()}
                    onKeyDown={handleKeyDown}
                    onMouseUp={() => checkActiveFormats()}
                    data-placeholder="Indtast indhold her..."
                  />
                  
                  <span className="form-help-text">
                    Brug formaterings-knapperne ovenfor til at formatere teksten. Du kan markere tekst og trykke på knapperne for at formatere den.
                  </span>
                </div>
                
                <div className="editor-actions">
                  <button 
                    className="save-content-btn"
                    onClick={handleSaveWelcomeText}
                  >
                    Gem Ændringer
                  </button>
                  <button 
                    className="cancel-edit-btn"
                    onClick={handleCancelEdit}
                  >
                    Annuller
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </main>
      
      {/* PhotoFocus modal */}
      {showPhotoFocus && focusImage && (
        <div className="modal-overlay">
          <PhotoFocus 
            image={focusImage} 
            onClose={() => setShowPhotoFocus(false)}
            onSave={handleSaveFocusPoint}
          />
        </div>
      )}
      
      <footer className="admin-footer">
        <p>&copy; {new Date().getFullYear()} Psykologisk Perspektiv - Admin Panel</p>
      </footer>
    </div>
  );
};

export default AdminForside;
